// src/pages/Modules.js
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/authContext';
import { useNavigate } from 'react-router-dom';

const PAGE_SIZE = 9;

function LevelBadge({ level }) {
  const label = level ? level[0].toUpperCase() + level.slice(1) : 'Beginner';
  const cls =
    level === 'advanced'
      ? 'bg-red-100 text-red-800 border-red-200'
      : level === 'intermediate'
      ? 'bg-amber-100 text-amber-800 border-amber-200'
      : 'bg-emerald-100 text-emerald-800 border-emerald-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${cls}`}>
      {label}
    </span>
  );
}

function AccessBadge({ status }) {
  if (status === 'unlocked') {
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs border bg-emerald-50 text-emerald-700 border-emerald-200">Unlocked</span>;
  }
  if (status === 'public') {
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs border bg-blue-50 text-blue-700 border-blue-200">Public</span>;
  }
  return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs border bg-gray-50 text-gray-700 border-gray-200">Locked</span>;
}

function ModuleCardSkeleton() {
  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden bg-white animate-pulse">
      <div className="h-36 w-full bg-gray-100" />
      <div className="p-4 space-y-3">
        <div className="h-5 bg-gray-100 rounded w-3/4" />
        <div className="h-4 bg-gray-100 rounded w-1/2" />
        <div className="h-4 bg-gray-100 rounded w-full" />
        <div className="h-4 bg-gray-100 rounded w-5/6" />
        <div className="h-8 bg-gray-100 rounded w-24" />
      </div>
    </div>
  );
}

export default function Modules() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]); // rows from modules_for_user
  const [error, setError] = useState('');

  // UI state
  const [query, setQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState('all'); // maps to dominant_level on server
  const [sortBy, setSortBy] = useState('recent');        // 'recent' | 'title' | 'lessons'
  const [page, setPage] = useState(1);

  const [codes, setCodes] = useState({}); // per-module code input

  // Load modules from RPC
  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      const { data, error: rpcErr } = await supabase.rpc('modules_for_user', {
        p_user_id: user.id,
        p_search: query || null,
        p_level: levelFilter === 'all' ? null : levelFilter,
        p_sort: sortBy,
        p_limit: 250,            // fetch a generous page from server
        p_offset: 0,
      });
      if (rpcErr) throw rpcErr;
      setModules(Array.isArray(data) ? data : []);
      setPage(1); // reset pagination when filters change
    } catch (e) {
      setError(e.message || 'Failed to load modules.');
    } finally {
      setLoading(false);
    }
  }, [user, query, levelFilter, sortBy]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    load();
  }, [user, navigate, load]);

  // Group by access status
  const myModules = useMemo(() => modules.filter(m => m.access_status === 'unlocked'), [modules]);
  const publicModules = useMemo(() => modules.filter(m => m.access_status === 'public'), [modules]);
  const lockedModules = useMemo(() => modules.filter(m => m.access_status === 'locked'), [modules]);

  // Client-side pagination helpers (on already filtered list from server)
  const paginate = (arr) => {
    const total = Math.max(1, Math.ceil(arr.length / PAGE_SIZE));
    const safePage = Math.min(page, total);
    const start = (safePage - 1) * PAGE_SIZE;
    return { items: arr.slice(start, start + PAGE_SIZE), total, safePage };
  };

  const { items: myItems, total: myTotal, safePage: myPage } = paginate(myModules);
  const { items: pubItems, total: pubTotal, safePage: pubPage } = paginate(publicModules);
  const { items: lockItems, total: lockTotal, safePage: lockPage } = paginate(lockedModules);

  const resetFilters = () => {
    setQuery('');
    setLevelFilter('all');
    setSortBy('recent');
    setPage(1);
  };

  // Unlock handler: try RPC unlock_module_with_code, else fallback to your original table flow
  const handleUnlock = async (moduleId) => {
    setError('');
    const code = (codes[moduleId] || '').trim();
    if (!code) {
      setError('Enter a valid code.');
      return;
    }

    // Try RPC first (if you created it)
    try {
      const { data: ok, error: rpcErr } = await supabase.rpc('unlock_module_with_code', {
        p_user_id: user.id,
        p_module_id: moduleId,
        p_code: code,
      });
      if (!rpcErr && ok === true) {
        // refresh list
        await load();
        setCodes(prev => ({ ...prev, [moduleId]: '' }));
        return;
      }
      // if rpc exists but returned false, show invalid
      if (!rpcErr && ok === false) {
        setError('Invalid or expired code.');
        return;
      }
      // else fall through to fallback
    } catch {
      // ignore and fallback
    }

    // Fallback to your original approach
    try {
      const { data: codeRow, error: codeErr } = await supabase
        .from('one_time_access_codes')
        .select('*')
        .eq('code', code)
        .eq('user_id', user.id)
        .eq('module_id', moduleId)
        .maybeSingle();

      if (codeErr || !codeRow) {
        setError('Invalid code.');
        return;
      }

      const { error: insErr } = await supabase
        .from('user_unlocked_modules')
        .insert({ user_id: user.id, module_id: moduleId });

      if (insErr) {
        setError('Failed to unlock module.');
        return;
      }

      await supabase
        .from('one_time_access_codes')
        .delete()
        .eq('id', codeRow.id);

      await load();
      setCodes(prev => ({ ...prev, [moduleId]: '' }));
    } catch (e) {
      setError('Failed to unlock module.');
    }
  };

  const Card = ({ mod, showUnlock }) => (
    <div className="rounded-lg border border-gray-200 overflow-hidden bg-white hover:shadow transition">
      {/* Placeholder cover; add an image field later if you want */}
      <div className="h-36 w-full bg-gradient-to-br from-indigo-50 to-purple-50" />
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-lg font-semibold text-gray-900">{mod.name}</h3>
          <div className="flex items-center gap-2">
            <LevelBadge level={mod.dominant_level} />
            <AccessBadge status={mod.access_status} />
          </div>
        </div>
        <p className="mt-2 text-sm text-gray-700">{mod.description || 'No description provided.'}</p>

        <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
          <span>{mod.lessons_count ?? 0} lesson{(mod.lessons_count ?? 0) === 1 ? '' : 's'}</span>
          {mod.module_updated_at && (
            <span>Updated {new Date(mod.module_updated_at).toLocaleDateString()}</span>
          )}
        </div>

        <div className="mt-4">
          {mod.access_status === 'unlocked' || mod.access_status === 'public' ? (
            <button
              className="bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1.5 rounded text-sm"
              onClick={() => navigate(`/modules/view/${mod.id}`)}
            >
              {mod.access_status === 'public' ? 'Explore' : 'View Lessons'}
            </button>
          ) : (
            showUnlock && (
              <div className="w-full">
                <p className="text-xs text-gray-500 mb-1">This module is locked. Enter access code:</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="border rounded px-3 py-1.5 text-sm w-48"
                    value={codes[mod.id] || ''}
                    onChange={(e) => setCodes(prev => ({ ...prev, [mod.id]: e.target.value }))}
                    placeholder="Enter code"
                  />
                  <button
                    className="bg-gray-800 hover:bg-gray-700 text-white px-3 py-1.5 rounded text-sm"
                    onClick={() => handleUnlock(mod.id)}
                  >
                    Unlock
                  </button>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );

  if (!user) return null;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Modules</h1>
          <p className="text-sm text-gray-600">Browse your unlocked content and available learning paths.</p>
        </div>
      </div>

      {/* Controls */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="grid lg:grid-cols-6 gap-3">
          <div className="lg:col-span-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              placeholder="Search by title or description"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
            />
          </div>
          <div className="lg:col-span-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">Level</label>
            <select
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>
          <div className="lg:col-span-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">Sort</label>
            <select
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="recent">Recently updated</option>
              <option value="title">Title A to Z</option>
              <option value="lessons">Most lessons</option>
            </select>
          </div>
          <div className="lg:col-span-1 flex items-end gap-2">
            <button
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50"
              onClick={resetFilters}
            >
              Reset
            </button>
            <button
              className="rounded-lg border border-gray-900 bg-gray-900 text-white px-3 py-2 text-sm hover:bg-black/90"
              onClick={load}
            >
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* My Modules */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">My Modules</h2>
          {myModules.length > PAGE_SIZE && (
            <div className="text-sm text-gray-700">Page {myPage} of {myTotal}</div>
          )}
        </div>
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: PAGE_SIZE }).map((_, i) => <ModuleCardSkeleton key={`m-${i}`} />)}
          </div>
        ) : myModules.length === 0 ? (
          <div className="rounded-lg border bg-white p-4 text-sm text-gray-700">No unlocked modules yet.</div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {myItems.map(mod => (
                <Card key={mod.id} mod={mod} showUnlock={false} />
              ))}
            </div>
            {myTotal > 1 && (
              <div className="flex items-center justify-center gap-2 mt-4">
                <button className="px-3 py-1.5 rounded border text-sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={myPage <= 1}>Prev</button>
                <button className="px-3 py-1.5 rounded border text-sm" onClick={() => setPage(p => p + 1)} disabled={myPage >= myTotal}>Next</button>
              </div>
            )}
          </>
        )}
      </section>

      {/* Public Modules */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">Available Modules</h2>
          {publicModules.length > PAGE_SIZE && (
            <div className="text-sm text-gray-700">Page {pubPage} of {pubTotal}</div>
          )}
        </div>
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: PAGE_SIZE }).map((_, i) => <ModuleCardSkeleton key={`p-${i}`} />)}
          </div>
        ) : publicModules.length === 0 ? (
          <div className="rounded-lg border bg-white p-4 text-sm text-gray-700">No public modules available.</div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {pubItems.map(mod => (
                <Card key={mod.id} mod={mod} showUnlock={false} />
              ))}
            </div>
            {pubTotal > 1 && (
              <div className="flex items-center justify-center gap-2 mt-4">
                <button className="px-3 py-1.5 rounded border text-sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={pubPage <= 1}>Prev</button>
                <button className="px-3 py-1.5 rounded border text-sm" onClick={() => setPage(p => p + 1)} disabled={pubPage >= pubTotal}>Next</button>
              </div>
            )}
          </>
        )}
      </section>

      {/* Locked Modules */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">Locked Modules</h2>
          {lockedModules.length > PAGE_SIZE && (
            <div className="text-sm text-gray-700">Page {lockPage} of {lockTotal}</div>
          )}
        </div>
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: PAGE_SIZE }).map((_, i) => <ModuleCardSkeleton key={`l-${i}`} />)}
          </div>
        ) : lockedModules.length === 0 ? (
          <div className="rounded-lg border bg-white p-4 text-sm text-gray-700">You have no locked modules.</div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {lockItems.map(mod => (
                <Card key={mod.id} mod={mod} showUnlock />
              ))}
            </div>
            {lockTotal > 1 && (
              <div className="flex items-center justify-center gap-2 mt-4">
                <button className="px-3 py-1.5 rounded border text-sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={lockPage <= 1}>Prev</button>
                <button className="px-3 py-1.5 rounded border text-sm" onClick={() => setPage(p => p + 1)} disabled={lockPage >= lockTotal}>Next</button>
              </div>
            )}
          </>
        )}
      </section>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
    </div>
  );
}
